import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import Select from "react-select";
import Swal from "sweetalert2";
import { getAllItemAlquiler } from "../../../api/Alquileres";
import { getAllPaciente } from "../../../api/pacienteApi";
import { formatMiles } from "../../../utils/miles";

export default function ModalAlquiler({ show, onHide, onSave, alquilerData, viewOnly = false }) {
  const [formData, setFormData] = useState({
    coditem: "",
    item: "",
    iditem: "",
    fechainicio: "",
    fechafin: "",
    factura: "",
    estado: "ACTIVO",
    obs: "",
    idusuario: "",
    usuario: "",
    idpresupuesto: "",
    monto: "",
  });

  const [items, setItems] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(false);

  // 🔹 Opciones de estado
  const estadoOptions = [
    { value: "ACTIVO", label: "ACTIVO" },
    { value: "FINALIZADO", label: "FINALIZADO" },
    { value: "CANCELADO", label: "CANCELADO" },
    { value: "DEVUELTO", label: "DEVUELTO" },
  ];

  // 🔹 Cargar ítems y usuarios
  useEffect(() => {
    if (show) cargarDatos();
  }, [show]);

  const cargarDatos = async () => {
    try {
      setLoading(true);
      const [resItems, resUsuarios] = await Promise.all([
        getAllItemAlquiler(),
        getAllPaciente(),
      ]);

      const disponibles = resItems.filter(
        (it) => it.estado?.toUpperCase() === "DISPONIBLE"
      );

      setItems(
        disponibles.map((it) => ({
          value: it.iditemalquiler,
          label: `${it.coditem} - ${it.item}`,
          coditem: it.coditem,
          item: it.item,
        }))
      );

      setUsuarios(
        resUsuarios.map((u) => ({
          value: u.IdPacientes,
          label: u.NombrePaciente,
        }))
      );
    } catch (err) {
      Swal.fire("Error", "No se pudieron cargar los datos", "error");
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Cargar datos si se está editando o viendo
  useEffect(() => {
    if (alquilerData) {
      setFormData({
        coditem: alquilerData.coditem || "",
        item: alquilerData.item || "",
        iditem: alquilerData.iditem || "",
        fechainicio: alquilerData.fechainicio || "",
        fechafin: alquilerData.fechafin || "",
        factura: alquilerData.factura || "",
        estado: alquilerData.estado || "ACTIVO",
        obs: alquilerData.obs || "",
        idusuario: alquilerData.idusuario || "",
        usuario: alquilerData.usuario || "",
        idpresupuesto: alquilerData.idpresupuesto || "",
        monto: alquilerData.monto || "",
      });
    } else {
      setFormData({
        coditem: "",
        item: "",
        iditem: "",
        fechainicio: "",
        fechafin: "",
        factura: "",
        estado: "ACTIVO",
        obs: "",
        idusuario: "",
        usuario: "",
        idpresupuesto: "",
        monto: "",
      });
    }
  }, [alquilerData]);

  // 🔹 Guardar
  const handleSave = () => {
    if (!formData.iditem || !formData.fechainicio) {
      Swal.fire("Atención", "Debe seleccionar un ítem y una fecha de inicio.", "warning");
      return;
    }

    const montoLimpio = String(formData.monto).replace(/\./g, "").replace(/,/g, "");

    const payload = {
      ...formData,
      monto: parseInt(montoLimpio) || 0,
      idpresupuesto:
        formData.idpresupuesto && formData.idpresupuesto !== ""
          ? parseInt(formData.idpresupuesto)
          : null,
    };

    onSave(payload);
    onHide();
  };

  // 🔹 Manejo Selects
  const handleSelectItem = (option) => {
    if (!option) return;
    setFormData((prev) => ({
      ...prev,
      iditem: option.value,
      coditem: option.coditem,
      item: option.item,
    }));
  };

  const handleSelectUsuario = (option) => {
    if (!option) return;
    setFormData((prev) => ({
      ...prev,
      idusuario: option.value,
      usuario: option.label,
    }));
  };

  const handleSelectEstado = (option) => {
    if (!option) return;
    setFormData((prev) => ({ ...prev, estado: option.value }));
  };

  // 🔹 Estilos react-select
  const customStyles = {
    control: (base) => ({
      ...base,
      borderColor: "#ced4da",
      minHeight: "38px",
      boxShadow: "none",
      backgroundColor: viewOnly ? "#f8f9fa" : "white",
      "&:hover": { borderColor: viewOnly ? "#ced4da" : "#86b7fe" },
    }),
    menu: (base) => ({
      ...base,
      zIndex: 9999,
    }),
  };

  return (
    <Modal show={show} onHide={onHide} centered backdrop="static" size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {viewOnly
            ? "Detalle del Alquiler"
            : alquilerData
            ? "Editar Alquiler"
            : "Nuevo Alquiler"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form>
          {/* Ítem y Usuario */}
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Ítem</Form.Label>
                <Select
                  options={items}
                  value={
                    formData.iditem
                      ? items.find((it) => it.value === formData.iditem)
                      : null
                  }
                  onChange={handleSelectItem}
                  placeholder={loading ? "Cargando ítems..." : "Seleccione un ítem"}
                  isClearable
                  styles={customStyles}
                  isDisabled={!!alquilerData || viewOnly}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Usuario / Paciente</Form.Label>
                <Select
                  options={usuarios}
                  value={
                    formData.idusuario
                      ? usuarios.find((u) => u.value === formData.idusuario)
                      : null
                  }
                  onChange={handleSelectUsuario}
                  placeholder={loading ? "Cargando usuarios..." : "Seleccione un usuario"}
                  isClearable
                  styles={customStyles}
                  isDisabled={viewOnly}
                />
              </Form.Group>
            </Col>
          </Row>

          {/* Fechas */}
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Fecha de Inicio</Form.Label>
                <Form.Control
                  type="date"
                  name="fechainicio"
                  value={formData.fechainicio}
                  disabled={viewOnly}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      fechainicio: e.target.value,
                    }))
                  }
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Fecha de Fin</Form.Label>
                <Form.Control
                  type="date"
                  name="fechafin"
                  value={formData.fechafin}
                  disabled={viewOnly}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      fechafin: e.target.value,
                    }))
                  }
                />
              </Form.Group>
            </Col>
          </Row>

          {/* Factura, Monto, Presupuesto y Estado */}
          <Row>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Factura</Form.Label>
                <Form.Control
                  type="text"
                  name="factura"
                  value={formData.factura}
                  disabled={viewOnly}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, factura: e.target.value }))
                  }
                  placeholder="N° de factura"
                />
              </Form.Group>
            </Col>

            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Monto (Gs)</Form.Label>
                <Form.Control
                  type="text"
                  name="monto"
                  value={formatMiles(formData.monto)}
                  disabled={viewOnly}
                  onChange={(e) => {
                    const raw = e.target.value.replace(/\D/g, "");
                    setFormData((prev) => ({ ...prev, monto: raw }));
                  }}
                  placeholder="Ej: 500.000"
                />
              </Form.Group>
            </Col>

            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>ID Presupuesto</Form.Label>
                <Form.Control
                  type="number"
                  name="idpresupuesto"
                  value={formData.idpresupuesto}
                  disabled={viewOnly}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, idpresupuesto: e.target.value }))
                  }
                  placeholder="Ej: 102"
                />
              </Form.Group>
            </Col>

            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Estado</Form.Label>
                <Select
                  options={estadoOptions}
                  value={estadoOptions.find(
                    (opt) => opt.value === formData.estado
                  )}
                  onChange={handleSelectEstado}
                  styles={customStyles}
                  isDisabled={!alquilerData || viewOnly}
                />
              </Form.Group>
            </Col>
          </Row>

          {/* Observaciones */}
          <Form.Group>
            <Form.Label>Observaciones</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              name="obs"
              value={formData.obs}
              disabled={viewOnly}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, obs: e.target.value }))
              }
              placeholder="Detalles adicionales del alquiler"
            />
          </Form.Group>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="danger" onClick={onHide}>
          {viewOnly ? "Cerrar" : "Cancelar"}
        </Button>
        {!viewOnly && (
          <Button variant="primary" onClick={handleSave}>
            {alquilerData ? "Guardar Cambios" : "Registrar Alquiler"}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}
